import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
  useTheme,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import Footer from '../components/Footer';

// Styled components
const PricingCard = styled(Card)(({ theme, featured }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  overflow: 'visible',
  border: featured ? `2px solid ${theme.palette.primary.main}` : `1px solid rgba(0, 0, 0, 0.12)`,
  backgroundColor: featured ? 'rgba(25, 118, 210, 0.04)' : 'white',
  ...(featured && {
    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)',
    transform: 'translateY(-8px)',
  }),
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    transform: featured ? 'translateY(-12px)' : 'translateY(-4px)',
  }
}));

const PricingPage = () => {
  const theme = useTheme();

  // Define pricing tiers
  const tiers = [
    {
      title: 'Basic',
      price: '12,499',
      description: 'For small cafes and food trucks',
      features: [
        { text: 'Single QR code', included: true },
        { text: 'Up to 5 menu pages', included: true },
        { text: 'Basic menu design', included: true },
        { text: 'Static images', included: true },
        { text: 'Standard support', included: true },
        { text: 'Multiple QR codes', included: false },
        { text: 'Custom branding', included: false },
      ],
      buttonText: 'Get Started',
      featured: false,
    },
    {
      title: 'Professional',
      price: '24,999',
      description: 'For established restaurants',
      features: [
        { text: 'Up to 3 QR codes', included: true },
        { text: 'Up to 15 menu pages', included: true },
        { text: 'Professional menu design', included: true },
        { text: 'Static images', included: true },
        { text: 'Priority support', included: true },
        { text: 'Custom branding', included: true },
        { text: '3D dish models', included: false },
      ],
      buttonText: 'Get Started',
      featured: true,
      mostPopular: true,
    },
    {
      title: 'Premium',
      price: '39,999',
      description: 'For restaurant chains and fine dining',
      features: [
        { text: 'Unlimited QR codes', included: true },
        { text: 'Unlimited menu pages', included: true },
        { text: 'Premium menu design', included: true },
        { text: '3D dish models (up to 5)', included: true },
        { text: 'Dedicated support', included: true },
        { text: 'Custom branding', included: true },
        { text: 'Analytics dashboard', included: true },
      ],
      buttonText: 'Get Started',
      featured: false,
    }
  ];

  return (
    <>
      <Box
        sx={{
          pt: { xs: 4, sm: 12 },
          pb: { xs: 8, sm: 16 },
          px: 2,
          textAlign: 'center',
          background: 'linear-gradient(180deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1" 
            gutterBottom
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography 
            variant="h5" 
            color="text.secondary" 
            sx={{ mb: 8, maxWidth: "700px", mx: "auto" }}
          >
            Choose the plan that's right for your restaurant and start engaging customers with AR menus
          </Typography>
          
          <Grid container spacing={4} alignItems="flex-start">
            {tiers.map((tier) => (
              <Grid 
                item 
                key={tier.title} 
                xs={12} 
                md={4}
                sx={{ 
                  display: 'flex',
                  ...(tier.featured && {
                    mt: { xs: 0, md: -3 },
                  })
                }}
              >
                <PricingCard featured={tier.featured} elevation={tier.featured ? 8 : 1}>
                  {tier.mostPopular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        borderRadius: 20,
                        px: 2,
                        py: 0.5,
                        fontWeight: 'medium',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <StarIcon fontSize="small" />
                      Most Popular
                    </Box>
                  )}
                  <CardHeader
                    title={tier.title}
                    titleTypographyProps={{ align: 'center', fontWeight: 'bold', variant: 'h5' }}
                    subheaderTypographyProps={{ align: 'center', color: 'text.secondary' }}
                    subheader={tier.description}
                    sx={{
                      pt: 4,
                      pb: 2,
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, px: 3, pb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', mb: 3 }}>
                      <Typography variant="h4" color="text.primary" fontWeight="bold">
                        ₹
                      </Typography>
                      <Typography variant="h2" color={tier.featured ? 'primary.main' : 'text.primary'} fontWeight="bold">
                        {tier.price}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ fontStyle: 'italic', mb: 3 }}>
                      One-time setup fee
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <List sx={{ py: 2 }} disablePadding>
                      {tier.features.map((feature) => (
                        <ListItem key={feature.text} disablePadding disableGutters sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 28, color: feature.included ? 'success.main' : 'text.disabled' }}>
                            {feature.included ? <CheckIcon /> : <CloseIcon />}
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature.text} 
                            primaryTypographyProps={{ 
                              color: feature.included ? 'text.primary' : 'text.disabled',
                              fontWeight: feature.included ? 'medium' : 'regular',
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                      component={RouterLink}
                      to="/contact"
                      fullWidth 
                      variant={tier.featured ? "contained" : "outlined"}
                      color={tier.featured ? "primary" : "primary"}
                      size="large"
                    >
                      {tier.buttonText}
                    </Button>
                  </CardActions>
                </PricingCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" fontWeight="bold" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Do I need to pay any monthly fees?
              </Typography>
              <Typography variant="body1">
                No, our pricing is straightforward with a one-time setup fee. There are no monthly subscriptions or hidden charges.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                How long does it take to set up?
              </Typography>
              <Typography variant="body1">
                Typically 3-5 business days from the time you provide all your menu details and images.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                What if I need to update my menu?
              </Typography>
              <Typography variant="body1">
                Menu updates are available at a small additional fee. Contact us for details on menu maintenance packages.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Do customers need to download an app?
              </Typography>
              <Typography variant="body1">
                No, your AR menu works directly in web browsers. Customers simply scan the QR code with their phone's camera.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" gutterBottom>
            Still have questions?
          </Typography>
          <Button 
            variant="contained" 
            component={RouterLink}
            to="/contact"
            size="large"
            sx={{ mt: 2 }}
          >
            Contact Our Team
          </Button>
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default PricingPage;